/**
 * Shared import validation framework — normalize rows for review UI + reports.
 *
 * Row shape used by ImportValidationReview:
 * {
 *   id, row, code, name, status: 'success'|'failed',
 *   reason, record (payload for import), raw (original sheet cells)
 * }
 */

export function fieldErrorsToReason(fieldErrors, fallback = "Validation failed") {
  if (fieldErrors && typeof fieldErrors === "object") {
    const parts = Object.entries(fieldErrors)
      .map(([f, m]) => `${f}: ${m}`)
      .filter(Boolean);
    if (parts.length) return parts.join("; ");
  }
  return fallback;
}

export function pickCodeName(record = {}) {
  const code =
    record["Emp Code*"] ??
    record["Emp Code"] ??
    record["Employee Code*"] ??
    record["Employee Code"] ??
    record.EmployeeCode ??
    record.EmpCode ??
    record.empCode ??
    record.employeeCode ??
    record.code ??
    record.Code ??
    record["Branch Name"] ??
    record.Name ??
    "";
  const name =
    record["Name*"] ??
    record.Name ??
    record.name ??
    record["Employee Name"] ??
    record.EmployeeName ??
    record["Branch Name"] ??
    String(code || "");
  return {
    code: code == null || code === "" ? "" : String(code),
    name: name == null || name === "" ? "" : String(name),
  };
}

/**
 * Build a unified validation result from API / client validators.
 */
export function buildImportValidationResult({
  validRecords = [],
  invalidRecords = [],
  sourceHeaders = [],
  /** optional: map excel row → original cell object for remarks export */
  rawByRow = null,
  /** When true, neither Import all nor Ignore-failed may proceed */
  hardBlockImport = false,
} = {}) {
  const successRows = (validRecords || []).map((rec, idx) => {
    const record = rec?.record && typeof rec.record === "object" ? rec.record : rec;
    const picked = pickCodeName(record);
    // Prefer top-level fields from API validators (e.g. bulk exit returns empCode/name)
    const code = rec?.empCode || rec?.employeeCode || picked.code;
    const name = rec?.name || picked.name;
    const row = rec?.row ?? rec?.__row ?? idx + 2;
    return {
      id: `ok-${row}-${idx}`,
      row,
      code,
      name,
      status: "success",
      reason:
        rec?.reason && rec.reason !== "OK" && rec.reason !== "Success"
          ? rec.reason
          : "Success",
      record: rec?.normalizedRecord || record,
      raw: rawByRow?.get?.(row) || record,
    };
  });

  const failedRows = (invalidRecords || []).map((rec, idx) => {
    const record = rec?.record && typeof rec.record === "object" ? rec.record : {};
    const picked = pickCodeName(record);
    const code = rec?.empCode || rec?.employeeCode || picked.code;
    const name = rec?.name || picked.name;
    const row = rec?.row ?? idx + 2;
    const reason = fieldErrorsToReason(
      rec?.fieldErrors,
      rec?.reason || rec?.error || "Validation failed"
    );
    return {
      id: `fail-${row}-${idx}`,
      row,
      code,
      name,
      status: "failed",
      reason,
      record,
      raw: rawByRow?.get?.(row) || record,
      fieldErrors: rec?.fieldErrors || {},
      hardBlock: !!rec?.hardBlock,
    };
  });

  const blocked =
    !!hardBlockImport ||
    failedRows.some((r) => r.hardBlock) ||
    (invalidRecords || []).some((r) => r?.hardBlock);

  return {
    successRows,
    failedRows,
    successCount: successRows.length,
    failedCount: failedRows.length,
    totalCount: successRows.length + failedRows.length,
    hardBlockImport: blocked,
    sourceHeaders: Array.isArray(sourceHeaders) ? sourceHeaders : [],
    allRows: [...successRows, ...failedRows].sort(
      (a, b) => Number(a.row) - Number(b.row)
    ),
  };
}

export function canImportAll(result) {
  return !!result && result.failedCount === 0 && result.successCount > 0;
}

export function canImportSuccessfulOnly(result) {
  return !!result && result.successCount > 0 && result.failedCount > 0;
}
